import React, { FC, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { Alert, Spin } from "antd";

interface ExcelViewerProps {
  excelUrl: string | null;
}

const ExcelViewer: FC<ExcelViewerProps> = ({ excelUrl }) => {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!excelUrl) {
      setHtmlContent("");
      setError(null);
      return;
    }

    const loadExcelFile = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(excelUrl);
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        const html = XLSX.utils.sheet_to_html(worksheet, {
          id: "excel-table",
          editable: false,
        });

        setHtmlContent(html);
      } catch (err) {
        setError("Failed to load Excel file");
      } finally {
        setIsLoading(false);
      }
    };

    loadExcelFile();
  }, [excelUrl]);

  if (!excelUrl) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spin size="large" tip="Loading Excel file..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        className="m-4"
      />
    );
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-lg overflow-hidden border border-gray-300">
      <style>
        {`
          .excel-viewer-container {
            max-height: calc(100vh - 400px);
            overflow: auto;
            background-color: #f0f0f0;
            padding: 0;
          }
          
          #excel-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            font-family: 'Calibri', 'Segoe UI', Arial, sans-serif;
            font-size: 11pt;
            background-color: white;
            table-layout: auto;
          }
          
          #excel-table th,
          #excel-table td {
            border: 1px solid #d0d0d0;
            padding: 4px 8px;
            text-align: left;
            min-width: 80px;
            height: 21px;
            line-height: 21px;
            vertical-align: middle;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          #excel-table th {
            background: linear-gradient(to bottom, #4472c4 0%, #2e5c9a 100%);
            color: white;
            font-weight: 700;
            position: sticky;
            top: 0;
            z-index: 20;
            border-right: 1px solid #2e5c9a;
            border-bottom: 2px solid #1f4788;
            text-align: center;
            font-size: 10pt;
            padding: 8px 10px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.15);
            text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          }
          
          #excel-table tbody tr {
            background-color: #f8f9fa;
          }
          
          #excel-table tbody tr:first-child {
            background: linear-gradient(to bottom, #4472c4 0%, #2e5c9a 100%);
          }
          
          #excel-table tbody tr:first-child td {
            background-color: transparent;
            color: white;
            font-weight: 700;
            text-align: center;
            padding: 8px 10px;
            border-right: 1px solid #2e5c9a;
            border-bottom: 2px solid #1f4788;
            text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          }
          
          #excel-table tbody tr:nth-child(even):not(:first-child) {
            background-color: #f0f2f5;
          }
          
          #excel-table tbody tr:hover:not(:first-child) {
            background-color: #e3f2fd;
          }
          
          #excel-table tbody td {
            background-color: inherit;
            color: #1a1a1a;
          }
          
          #excel-table tbody tr:hover:not(:first-child) td {
            background-color: #e3f2fd;
          }
          
          /* Excel-like cell selection effect */
          #excel-table td:hover {
            outline: 2px solid #217346;
            outline-offset: -1px;
            position: relative;
            z-index: 1;
          }
          
          /* Scrollbar styling to match Excel */
          .excel-viewer-container::-webkit-scrollbar {
            width: 17px;
            height: 17px;
          }
          
          .excel-viewer-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border: 1px solid #d4d4d4;
          }
          
          .excel-viewer-container::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border: 1px solid #a8a8a8;
          }
          
          .excel-viewer-container::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
          
          .excel-viewer-container::-webkit-scrollbar-corner {
            background: #f1f1f1;
          }
          
          /* Excel header bar */
          .excel-header-bar {
            background: linear-gradient(to bottom, #ffffff 0%, #e5e5e5 100%);
            border-bottom: 1px solid #c0c0c0;
            padding: 8px 12px;
            font-family: 'Segoe UI', Arial, sans-serif;
            font-size: 11pt;
            color: #333;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .excel-icon {
            width: 20px;
            height: 20px;
            background: #217346;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 12px;
            border-radius: 2px;
          }
          
          /* Number formatting for numeric cells */
          #excel-table td[data-t="n"] {
            text-align: right;
            font-variant-numeric: tabular-nums;
          }
        `}
      </style>
      <div
        className="excel-viewer-container"
        /* eslint-disable-next-line react/no-danger */
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    </div>
  );
};

export { ExcelViewer };
